import type { ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { GET_ALL_CONFIGS } from '../gql/queries/appConfig';
import { isFeatureEnabled } from '../hooks/useFeatureFlag';
import { readPhotoCapture, type PhotoCapture } from './exif/readPhotoCapture';
import { PHOTO_GEOTAG_FLAG } from './photoGeotagFlag';
import { resolvePkAtPosition, type PkResolution } from './resolvePkAtPosition';
import { directionNameKey } from './surveillanceRoute';
import { pkFormatter } from './transform';

/**
 * Dónde se hizo una foto: lo comparten el visor (§ 16) y los informes PDF
 * (§ 19). Spec 2026-09-23 fotos geolocalizadas.
 */

/** Lo que devuelve el servidor en `Image.capture` (§ 18). */
export type ServerCaptureStatus = 'PENDING' | 'READ' | 'UNAVAILABLE';

export interface ServerPhotoCapture extends PhotoCapture {
  status: ServerCaptureStatus;
}

/** Lo mínimo de una imagen para saber dónde se hizo. */
export interface LocatableImage {
  publicUrl?: string | null;
  /** Solo lo traen las consultas que lo piden (front #302, con el backend de § 18). */
  capture?: ServerPhotoCapture | null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * La ubicación de una foto: la del servidor si ya la leyó (`READ`); si no, el
 * EXIF del original, leído aquí (128 KB). `null` si no hay de dónde leerla
 * (foto legacy de Cloudinary sin dato en el servidor).
 *
 * PENDING (el worker aún no pasó) y UNAVAILABLE (procesada antes de § 18)
 * caen al original: así la foto recién subida enseña su ubicación igual, y la
 * app funciona contra un backend con o sin el campo (§ 18.1 D39).
 */
export async function loadPhotoCapture(
  image: LocatableImage | null | undefined,
  signal?: AbortSignal,
): Promise<PhotoCapture | null> {
  const server = image?.capture;
  if (server?.status === 'READ') {
    return {
      latitude: server.latitude,
      longitude: server.longitude,
      accuracyMeters: server.accuracyMeters,
      takenAtLocal: server.takenAtLocal,
      roisCamera: server.roisCamera,
    };
  }
  if (!image?.publicUrl) return null;
  return readPhotoCapture(image.publicUrl, signal);
}

// ─── Formatos ─────────────────────────────────────────────────────────────────

/** «2026:09:24 17:25:41» (EXIF) → «24/09/2026 17:25». */
export const formatTakenAt = (exifDate: string | null | undefined): string | null => {
  const m = exifDate?.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : null;
};

/** 5 decimales ≈ 1 m: más precisión que la del GPS es ruido. */
export const formatCoords = (latitude: number, longitude: number): string =>
  `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;

/** URL universal de Google Maps (en Android la abre la app de Maps). */
export const mapsUrl = (latitude: number, longitude: number): string =>
  `https://www.google.com/maps/search/?api=1&query=${latitude.toFixed(5)},${longitude.toFixed(5)}`;

/** «480 m» o «10,3 km», con el separador decimal del idioma de la app. */
export const formatDistance = (meters: number, language: string): string =>
  meters < 1000
    ? `${Math.round(meters)} m`
    : `${(meters / 1000).toLocaleString(language, { maximumFractionDigits: 1 })} km`;

/** Clave del sentido solo si es uno de los dos de la vía; «ambos» o nada, se omite. */
export const directionKeyToShow = (direction: string | null | undefined): string | null => {
  const key = directionNameKey(direction);
  return key === 'pages:surveillance.directionAscending' ||
    key === 'pages:surveillance.directionDescending'
    ? key
    : null;
};

// ─── Informes PDF (§ 19) ──────────────────────────────────────────────────────

/**
 * El pie de ubicación de una foto en un PDF. Puro.
 *
 * Línea 1, si hay vía: «C-25 · PK 150 + 300 · Sentido ascendente» (también
 * fuera del rango declarado: aquí no se rellena nada y el PK es la mejor
 * referencia). Línea 2: coordenadas, hora de la cámara y precisión. Lejos de
 * toda vía, solo la línea 2: nunca un PK inventado. Sin GPS, nada: no se
 * escribe «sin ubicación» en cada foto.
 */
export function describePhotoLocation(
  capture: PhotoCapture,
  resolution: PkResolution | null,
  t: Translate,
): string[] {
  const { latitude, longitude } = capture;
  if (latitude === null || longitude === null) return [];

  const lines: string[] = [];
  if (resolution && (resolution.kind === 'ok' || resolution.kind === 'out-of-range')) {
    const { roadName, pkKm, pkMeter, direction } = resolution.match;
    const dirKey = directionKeyToShow(direction);
    lines.push(
      t('modals:photoLocation.roadPk', { road: roadName, pk: pkFormatter(pkKm, pkMeter, true) }) +
        (dirKey ? ` · ${t(dirKey)}` : ''),
    );
  }
  lines.push(
    [
      formatCoords(latitude, longitude),
      formatTakenAt(capture.takenAtLocal),
      capture.accuracyMeters !== null
        ? t('modals:photoLocation.accuracy', { meters: Math.round(capture.accuracyMeters) })
        : null,
    ]
      .filter(Boolean)
      .join(' · '),
  );
  return lines;
}

export interface PhotoLocationContext {
  client: ApolloClient<unknown>;
  t: Translate;
  /** Un PK por coordenada (5 decimales): las fotos de un mismo sitio no repiten la búsqueda. */
  pkByCoords: Map<string, Promise<PkResolution | null>>;
}

/**
 * Contexto para escribir la ubicación en un informe, o `null` si el tenant no
 * tiene el flag `feature-photo-geotag` (§ 19 D44) o no se puede saber: un
 * informe nunca se rompe ni se retrasa por esto.
 */
export async function createPhotoLocationContext(
  client: ApolloClient<unknown>,
  t: Translate,
): Promise<PhotoLocationContext | null> {
  try {
    const { data } = await client.query<{ getAllConfigs: Record<string, string> | null }>({
      query: GET_ALL_CONFIGS,
      fetchPolicy: 'cache-first',
      errorPolicy: 'ignore',
    });
    const flags = data?.getAllConfigs ?? {};
    if (!isFeatureEnabled(flags, PHOTO_GEOTAG_FLAG)) return null;
  } catch {
    return null;
  }
  return { client, t, pkByCoords: new Map() };
}

/**
 * Pie de ubicación de una foto para un informe, o `undefined` si no tiene
 * (sin GPS, o no se pudo leer: la foto sale igual, sin pie).
 */
export async function photoLocationLines(
  image: LocatableImage | null | undefined,
  ctx: PhotoLocationContext,
): Promise<string[] | undefined> {
  let capture: PhotoCapture | null;
  try {
    capture = await loadPhotoCapture(image);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: 'photo-location', step: 'report' },
      level: 'warning',
    });
    return undefined;
  }
  if (!capture || capture.latitude === null || capture.longitude === null) return undefined;

  const { latitude, longitude } = capture;
  const key = formatCoords(latitude, longitude);
  let pending = ctx.pkByCoords.get(key);
  if (!pending) {
    pending = resolvePkAtPosition(ctx.client, { lat: latitude, lng: longitude }).catch(() => null);
    ctx.pkByCoords.set(key, pending);
  }
  const lines = describePhotoLocation(capture, await pending, ctx.t);
  return lines.length > 0 ? lines : undefined;
}
